import axios from "axios";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_BASE = BACKEND ? `${BACKEND}/api` : "/api";
if (typeof document !== "undefined") {
  const absoluteApiBase = BACKEND
    ? API_BASE
    : new URL(API_BASE, window.location.origin).href;
  document.documentElement.dataset.shoptosaApi = absoluteApiBase.replace(/\/$/, "");
}

export const api = axios.create({ baseURL: API_BASE });

// The console keeps the bearer token in localStorage and sends Authorization.
// Cookie sessions exist behind SHOPTOSA_SESSION_COOKIES, with a CSRF cookie.
// Leave withCredentials off until that API deploy is what production serves:
// credentialed browser calls are rejected when the API answers with
// Access-Control-Allow-Origin: *.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aura_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PROXY_IMG_HOSTS = /(^|\.)walmartimages\.com$|(^|\.)etsystatic\.com$|(^|\.)media-amazon\.com$/i;

export const imgUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) {
    try {
      const host = new URL(src).hostname || "";
      if (PROXY_IMG_HOSTS.test(host)) {
        return `${API_BASE}/img?u=${encodeURIComponent(src)}`;
      }
    } catch {
      /* keep original */
    }
    return src;
  }
  return `${BACKEND}${src}`;
};

export const wsUrl = (path) => {
  if (BACKEND) return `${BACKEND.replace(/^http/, "ws")}${path}`;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}${path}`;
};

const PARTNER_SOURCES = new Set([
  "channel3", "rakuten", "cj", "awin", "flexoffers", "amazon_associates",
  "ebay", "walmart_affiliate", "skimlinks", "target", "bestbuy", "admitad", "flipkart_affiliate", "quickcommerce", "involve_asia", "india_crawlee", "earnkaro", "partner",
]);

export const MARKETPLACE_LABELS = {
  ebay: "eBay",
  amazon_associates: "Amazon",
  walmart_affiliate: "Walmart",
  target: "Target",
  bestbuy: "Best Buy",
  rakuten: "Rakuten",
  cj: "CJ",
  awin: "Awin",
  flexoffers: "FlexOffers",
  skimlinks: "Skimlinks",
  admitad: "Admitad",
  flipkart_affiliate: "Flipkart",
};

export function marketplaceName(source, id) {
  if (MARKETPLACE_LABELS[source]) return MARKETPLACE_LABELS[source];
  const raw = String(id || "");
  if (raw.startsWith("ebay:")) return "eBay";
  return "";
}

export const isPartnerListing = (node = {}, offer = {}) => {
  const source = node.source || offer.source || "";
  const id = String(node.id || offer.id || "");
  return PARTNER_SOURCES.has(source) || /^(channel3|rakuten|cj|awin|flexoffers|paapi|ebay|walmart|skimlinks|target|bestbuy|admitad|flipkart|qc):/.test(id);
};

export const graphProductToCard = (node) => {
  if (!node || typeof node !== "object") {
    return { id: "", title: "", images: [], brand: "", brand_id: "", external: false };
  }
  const offer = node.offers?.[0] || {};
  const source = node.source || offer.source || "";
  const external = isPartnerListing(node, offer);
  const market = marketplaceName(source, node.id);
  return {
    id: external ? node.id : offer.id,
    title: node.title,
    description: node.description || "",
    images: (node.images || []).map((img) => img.url || img),
    image: node.card_image || "",
    category: node.category,
    brand: node.brand || "",
    brand_id: node.brand_id || "",
    price: offer.price ?? node.min_price,
    currency: offer.currency || "USD",
    mrp_inr: node.mrp_inr,
    discount_pct: node.discount_pct,
    origin_country: offer.origin_country,
    ships_to: offer.ships_to,
    seller_name: node.brand || (node.offer_count > 1 ? `${node.offer_count} sellers` : offer.seller_name),
    store: market || offer.seller_name || node.brand || (external ? "Partner store" : "Shoptosa"),
    is_live: node.is_live || node.offers?.some((o) => o.is_live),
    offer_count: node.offer_count || node.offers?.length || 1,
    source: external ? (PARTNER_SOURCES.has(source) ? source : "channel3") : "aura",
    buy_url: offer.buy_url || "",
    affiliate: Boolean(node.affiliate || offer.affiliate),
    external,
    image_fit: node.image_fit || "",
  };
};

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  if (!e?.response) {
    return e?.code === "ECONNABORTED"
      ? "The AI request timed out. Try again in a minute."
      : "Could not reach the API. Check your connection and try again — if it persists, the server may be down or the request was blocked.";
  }
  return e?.message || "Something went wrong";
};
